import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import { Kbd, TextArea, Button, Banner, Chip } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, FormattedList, useIntl} from 'react-intl'
import { useBrand } from 'contexts/BrandContext'
import { rebrandText } from 'brands/rebrand'
import Icon from 'components/Icon'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import messages from './messages'
import { ContactCard } from 'components/ContactInfo'
import Waveform from 'components/Waveform'
import DemoPlayButton from 'containers/DemoPlayButton'
import useSyntheticVoice from 'hooks/useSyntheticVoice'
import { saveAs } from 'file-saver'
import { getVoicesUrl, getVoiceUrl } from 'data/voices/list'
import useApi from 'hooks/useApi'
import { transformVoices } from 'utils'
import genderNames from 'data/genders/messages'
import languageNames from 'data/languages/messages'
import classNames from 'classnames'
import statementImg from './statement.jpg'
import useOpenAi from 'hooks/useOpenAi'

const AiPage = ({ project }) => {
  const { voiceId } = useParams()
  const { formatMessage, locale } = useIntl()
  const brand = useBrand()
  
  const navigate = useNavigate()

  const [syntheticVoiceState, scriptToVoice] = useSyntheticVoice()
  const [openAiState, voiceSuggest, moderateContent, suggestVoiceOverScript] = useOpenAi()

  const [form, setForm] = useState({
    script: '',
  });

  const [rawData, loading, errors, refetch, meta] = useApi(
    getVoicesUrl(project, project.order),
    { load: false }
  );

  const data = useMemo(() => transformVoices(rawData), [rawData])

  const [syntheticVoice, syntheticVoiceLoading, syntheticVoiceErrors, fetchSyntheticVoice] = useApi(null, {
    load: false,
  })
  const [syntheticVoices, syntheticVoicesLoading, syntheticVoicesErrors, fetchSyntheticVoices] = useApi(null, {
    load: false,
  })
  const [selectedVoice, setSelectedVoice] = useState(null);

  const [history, setHistory] = useState([]);

  useKeyboardShortcut(['Escape'], () => navigate('..'), {
    overrideSystem: true,
  });

  useEffect(() => {
    if (voiceId) {
      refetch(getVoiceUrl(voiceId))
      fetchSyntheticVoice(`/rest/v2/supplier/${voiceId}/synthetic-voice`)
    } else {
      fetchSyntheticVoices('/rest/v2/syntheticvoice')
    }
  }, [voiceId])

  useEffect(() => {
    if (selectedVoice) {
      refetch(getVoiceUrl(selectedVoice))
      fetchSyntheticVoice(`/rest/v2/supplier/${selectedVoice}/synthetic-voice`)
    }
  }, [selectedVoice]);

  useEffect(() => {
    if (syntheticVoices && syntheticVoices.length > 0) {
      setSelectedVoice(syntheticVoices[0].sku)
    }
  }, [syntheticVoices]);

  useEffect(() => {
    if (syntheticVoice && syntheticVoice.length > 0) {
      if (syntheticVoice[0].approved === "0") {
        navigate('..');
      }
    }
  }, [syntheticVoice]);

  async function generateVoice () {
    const moderation = await moderateContent(form.script);
    if (!moderation.isFlagged && !moderation.containsProfanity && syntheticVoice.length > 0) {
      const url = await scriptToVoice(syntheticVoice[0].elevenlabs_voice_id, form.script);
      setHistory((prev) => [{ script: form.script, url }, ...prev]);
      setForm({ ...form, script: '' });

      // To scroll to the bottom of the page
      // and make the new voice visible
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      });
    }
  }

  async function suggestScript() {
    if (openAiState.isSuggesting == false) {
      const script = await suggestVoiceOverScript();
      setForm({ ...form, script });
    }
  }

  return (
    <div className="h-full flex flex-col md:flex-row divide-x divide-gray-300 h-full">
      <div className="w-full md:w-1/3">
        <div className="p-4 md:p-8 space-y-4 md:space-y-8">
          <div className="flex">
            <div>
              <div className="space-y-4">
                <Link to=".." className="space-x-1 text-primary font-medium text-sm md:hidden">
                  <Icon icon="angle-left" />
                  <FormattedMessage {...messages.goBack} tagName="span" />
                </Link>

                { data && !selectedVoice &&
                <div
                  className="p-4 bg-gray-50 rounded border border-gray-200 space-y-6"
                  style={{ backgroundColor: '#F8F5EE' }}
                >
                    <div className="space-y-1">
                      <Chip state="success">
                        Original voice talent <Icon icon={['fas', 'star']} />
                      </Chip>

                      <h1 className="text-xl font-medium">
                        <FormattedMessage {...messages.sku} values={{ sku: data[0].sku }} />
                      </h1>
                    </div>

                    <FormattedMessage
                      {...messages.collaboration}
                      tagName="p"
                      values={{
                        sku: data[0].sku,
                      }}
                    />

                    <FormattedMessage
                      {...messages.nativeIn}
                      tagName="p"
                      values={{
                        age: data[0].age,
                        sku: data[0].sku,
                        gender: formatMessage(genderNames[data[0].gender]).toLowerCase(),
                        languages: (
                          <span className="lowercase">
                            <FormattedList
                              type="conjunction"
                              value={data[0].languages.map((langTag) =>
                                langTag in languageNames ? formatMessage(languageNames[langTag]) : langTag
                              )}
                            />
                          </span>
                        ),
                      }}
                    />

                    <FormattedMessage {...messages.listenTo} tagName="p" />

                    {(syntheticVoice && syntheticVoice.length > 0) && (
                      <div className="flex">
                        <Waveform src={syntheticVoice[0].supplier_demo_url} />

                        <div className="flex">
                          <DemoPlayButton
                            childClassName=""
                            demo={{ url: syntheticVoice[0].supplier_demo_url }}
                          />
                        </div>
                      </div>
                    )}

                    <div className="w-1/3 md:w-full 2xl:w-1/3">
                      <Button
                        state="positive"
                        variant="solid"
                        className="w-full space-x-1"
                        onClick={() => {
                          navigate(`../${voiceId}`)
                        }}
                      >
                        <FormattedMessage
                          {...messages.view}
                          tagName="span"
                          values={{ sku: data[0].sku }}
                        />
                      </Button>
                    </div>
                  </div>
                }

                <div className="flex items-center space-x-2 border border-gray-200 rounded">
                  <img className="w-1/3" src={statementImg} />
                  <FormattedMessage
                    {...messages.statement}
                    tagName="p"
                    values={{
                      link: (chunks) => <a
                        className="text-primary hover:underline"
                        href={rebrandText(brand, 'https://www.voicearchive.com/ai-synthetic-voices-and-voice-cloning/')}
                        target="_blank"
                        rel="noopener noreferrer"
                      >{chunks}</a>,
                    }}
                  />
                </div>

                <div className="grid border border-gray-200 rounded">
                  <ContactCard compact />
                </div>

                { !voiceId && syntheticVoices?.length && syntheticVoices.map((syntheticVoice, index) => (
                   <div key={'syn_' + index} className={classNames('p-4 border rounded', {
                     'border-primary': selectedVoice === syntheticVoice.sku,
                     'border-gray-200': selectedVoice !== syntheticVoice.sku,
                   })}>
                      <a href={`ai/${syntheticVoice.sku}`}>
                        <h3 className="block text-base text-gray-700">
                          {syntheticVoice.sku}
                        </h3>
                      </a>

                     <div className="flex">
                       <Waveform src={syntheticVoice.synthetic_demo_url} />

                       <div className="flex space-x-1">
                         <DemoPlayButton
                           childClassName=""
                           demo={{ url: syntheticVoice.synthetic_demo_url }}
                         />
                         { (selectedVoice !== syntheticVoice.supplier_uid) ? (
                         <Button
                           variant="plain"
                           className="inline-block"
                           size="sm"
                           onClick={() =>
                            setSelectedVoice(syntheticVoice.sku)
                           }
                         >
                           <Icon icon="plus" /> <span className="hidden sm:inline"><FormattedMessage {...messages.select} /></span>
                         </Button>
                         ) : null}
                       </div>
                     </div>
                   </div>
                 )) }


              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-48 md:w-2/3 flex flex-col p-8 space-y-10">
        <h1 className="text-3xl font-bold text-center text-gray-800">
          <FormattedMessage {...messages.title} />
        </h1>

        <h2 className="text-xl font-bold text-center text-gray-800">
          <FormattedMessage {...messages.subtitle} />
        </h2>

        <h3 className="text-base text-center w-2/3 m-auto">
          <FormattedMessage {...messages.description} />
        </h3>

        <div className="md:flex md:space-x-6 items-center">
          <div className="md:w-1/2 w-full p-4 border border-primary rounded bg-gray-50 space-y-2">
            <h4 className="font-bold text-primary">Selected voice characterics</h4>

            <ul className="list-disc list-inside">
              { data ? (
                <>
                  <li>
                    <FormattedMessage {...messages.age} values={{ age: data[0].age }} />
                  </li>
                  <li>
                    <FormattedMessage {...genderNames[data[0].gender] } />
                  </li>
                </>
              ): loading ?? null }
            </ul>
          </div>
        </div>

        <div className='text-right'>
          <TextArea
            className="text-sm"
            placeholder={formatMessage(messages.scriptPlaceholder)}
            rows={4}
            maxLength={200}
            value={form.script}
            disabled={openAiState.isLoading || syntheticVoiceState.isLoading}
            onChange={(e) => {
              setForm({ ...form, script: e.target.value });
            }}
          />
          <span className='text-sm text-positive cursor-pointer' onClick={() => suggestScript()}>
            { openAiState.isSuggesting ? formatMessage(messages.suggestionLoading) : formatMessage(messages.suggestionButton) }
          </span>
        </div>

        { !openAiState.isLoading && openAiState.isFlagged ?
          <span className='text-negative hover:text-negative-lighter'><FormattedMessage {...messages.scriptFlagged} /></span>
          : null
        }

        { !openAiState.isLoading && openAiState.containsProfanity ?
          <span className='text-negative hover:text-negative-lighter'><FormattedMessage {...messages.scriptProfanity} /></span>
          : null
        }

        <div className="flex justify-between items-center">
          <p className="w-2/3 text-sm">
            <FormattedMessage
              {...messages.legalNotice}
              values={{ tcs: (chunks) => <a className="text-primary" href={rebrandText(brand, 'https://www.voicearchive.com/terms-conditions/')}>{chunks}</a> }}
            />
          </p>

          <Button
            className="flex-shrink-0"
            onClick={() => generateVoice()}
            disabled={openAiState.isLoading || syntheticVoiceState.isLoading || (!syntheticVoice || syntheticVoice.length === 0)}
          >
            { openAiState.isLoading || syntheticVoiceState.isLoading ?
              <span><FormattedMessage {...messages.processing} /></span>
            :
              <span><FormattedMessage {...messages.generateVoice} /></span>
            }
          </Button>
        </div>

        <div>
          { history.map((item, index) => (
            <div className="my-4 p-4 border border-primary rounded bg-gray-50" key={index}>
              <h3 className="font-bold">{voiceId}</h3>
              <p>{item.script}</p>
              <div className="flex">
                <Waveform src={item.url} waveColor='#a7c5de' />

                <div className="flex items-center space-x-1 flex-shrink-0">

                  {/* <Button
                    variant="plain"
                    className="inline-block"
                    size="sm"
                    onClick={() =>
                      saveAs(item.url, 'ai-voice.mp3')
                    }
                  >
                    <Icon icon="download" /> <span className="hidden sm:inline"><FormattedMessage {...messages.download} /></span>
                  </Button> */}

                  <DemoPlayButton
                    childClassName=""
                    demo={{ url: item.url }}
                  />
                </div>
              </div>
            </div>

          ))}
        </div>
      </div>
    </div>
  )
}

export default AiPage
